import React, { useEffect, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import useAppSelector from '../app/redux/useAppSelector';
import { usePostTOC } from './PostTOCContext';

const useOnMainEditorResize = (handler: () => void) => {
  const { isTOCOpen } = usePostTOC();
  const didMountRef = useRef(false);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const rightPanelOpen = useAppSelector((state) => state.ui.rightPanel.open);
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [sidebarOpen] = useDebounce(
    useAppSelector((state) => state.ui.isSidebarOpen),
    // Sync with #sidebar's transition duration
    150,
  );

  useEffect(() => {
    if (didMountRef.current) {
      handlerRef.current();
    } else {
      didMountRef.current = true;
    }
  }, [rightPanelOpen, sidebarOpen, isTOCOpen]);

  useEffect(() => {
    const listener = () => handlerRef.current();
    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
    };
  }, []);
};

interface MainEditorResizeHandlerProps {
  onMainEditorResize: () => void;
  children: React.ReactNode;
}

export const MainEditorResizeHandler = ({
  onMainEditorResize,
  children,
}: MainEditorResizeHandlerProps) => {
  useOnMainEditorResize(onMainEditorResize);
  return <>{children}</>;
};

export default useOnMainEditorResize;
